import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { Box, Button, Typography, useTheme } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faChartLine,
  faShieldHalved,
  faUsersGear
} from "@fortawesome/free-solid-svg-icons";

import Link from "../../atoms/mui/Link";
import DashboardLayout from "../layouts/DashboardLayout";
import CommunicationCenter from "../../modules/CommunicationCenter";
import Hero from "../upgrade/sections/Hero";
import PricingGrid from "../upgrade/sections/PricingGrid";
import WhyComparison from "../upgrade/sections/WhyComparison";
import FinalCTA from "../upgrade/sections/FinalCTA";
import ScoutFab from "../scout/ScoutFab";

import { analyticsTrack } from "../../../helpers/analytics";

const ACLYMATE_URL = "https://aclymate.com";
const HERO_CALLOUTS = [
  {
    icon: faChartLine,
    title: "Software",
    description: "Carbon accounting and sustainability management tools."
  },
  {
    icon: faUsersGear,
    title: "Services",
    description: "Expert guidance, bookkeeping, and data support."
  },
  {
    icon: faShieldHalved,
    title: "Credibility",
    description: "Reporting, certifications, offsets, and RECs."
  }
];

const COMPARISON_COLUMNS = [
  {
    key: "navigator",
    label: "Aclymate Navigator",
    description: "Core platform"
  },
  {
    key: "aclymate-one",
    label: "Aclymate One",
    description: "Software plus expert services",
    highlighted: true
  },
  {
    key: "aclymate-custom",
    label: "Aclymate Custom",
    description: "Tailored advanced needs"
  }
];

const COMPARISON_ROWS = [
  "Best for",
  "Ideal team",
  "Carbon accounting",
  "Scope 1, 2, 3",
  "Guided data collection",
  "Dashboards and reporting",
  "Sustainability management",
  "Integrations",
  "OnePage access",
  "Expert services",
  "Certification support",
  "Offsets & RECs",
  "Custom reporting"
];

const COMPARISON_VALUES = [
  {
    navigator: "Core platform",
    "aclymate-one": "Software plus expert services",
    "aclymate-custom": "Tailored advanced needs"
  },
  {
    navigator: "Growing teams with reporting needs",
    "aclymate-one": "Lean teams that need help doing the work",
    "aclymate-custom": "Larger or more complex programs"
  },
  {
    navigator: "Included",
    "aclymate-one": "Included",
    "aclymate-custom": "Included"
  },
  {
    navigator: "Full",
    "aclymate-one": "Full",
    "aclymate-custom": "Full"
  },
  {
    navigator: "Included",
    "aclymate-one": "Included",
    "aclymate-custom": "Included"
  },
  {
    navigator: "Included",
    "aclymate-one": "Included",
    "aclymate-custom": "Advanced"
  },
  {
    navigator: "Included",
    "aclymate-one": "Included",
    "aclymate-custom": "Advanced"
  },
  {
    navigator: "Expanded",
    "aclymate-one": "Expanded",
    "aclymate-custom": "Tailored"
  },
  {
    navigator: "Limited / roadmap",
    "aclymate-one": "Included",
    "aclymate-custom": "Included"
  },
  {
    navigator: "—",
    "aclymate-one": "Included",
    "aclymate-custom": "Included"
  },
  {
    navigator: "Moderate",
    "aclymate-one": "Included",
    "aclymate-custom": "Advanced"
  },
  {
    navigator: "Included",
    "aclymate-one": "Included",
    "aclymate-custom": "Included"
  },
  {
    navigator: "Limited",
    "aclymate-one": "Included",
    "aclymate-custom": "Advanced"
  }
];

const HeroCalloutCard = ({ callout, palette }) => (
  <Box
    sx={{
      display: "flex",
      gap: 1.5,
      p: 2,
      borderRadius: 2,
      bgcolor: palette.backgroundGray.light,
      border: `1px solid ${palette.backgroundGray.dark}`
    }}
  >
    <Box
      sx={{
        width: 40,
        height: 40,
        borderRadius: "50%",
        bgcolor: palette.backgroundGray.main,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0
      }}
    >
      <FontAwesomeIcon
        icon={callout.icon}
        style={{ color: palette.primary.main, fontSize: 18 }}
      />
    </Box>
    <Box>
      <Typography variant="subtitle2" color="textPrimary">
        {callout.title}
      </Typography>
      <Typography variant="caption" color="textSecondary">
        {callout.description}
      </Typography>
    </Box>
  </Box>
);

const HeroCalloutVisual = () => {
  const { palette } = useTheme();
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      {HERO_CALLOUTS.map((c) => (
        <HeroCalloutCard key={c.title} callout={c} palette={palette} />
      ))}
    </Box>
  );
};

const BackToDashboard = () => (
  <Link href="/explorer/dashboard">
    <Button
      variant="text"
      color="primary"
      size="small"
      startIcon={
        <FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 12 }} />
      }
    >
      Back to dashboard
    </Button>
  </Link>
);

const buildPlans = (openSales) => [
  {
    id: "navigator",
    name: "Aclymate Navigator",
    label: "Core Platform",
    bestFor: "Growing businesses that need the paid platform",
    description:
      "Carbon accounting, reporting, and sustainability management in one platform.",
    bullets: [
      "Scope 1, 2, and 3 carbon accounting",
      "Guided workflows and data collection",
      "Dashboards and progress tracking",
      "Sustainability management foundation",
      "Integrations and stronger reporting",
      "Offset marketplace access"
    ],
    primaryCta: {
      label: "Start Navigator Trial",
      href: "/account-creation/company"
    },
    secondaryCta: { label: "See Demo", href: `${ACLYMATE_URL}/demo` }
  },
  {
    id: "aclymate-one",
    name: "Aclymate One",
    label: "Most Popular",
    highlighted: true,
    bestFor: "Teams that want software plus expert services",
    description:
      "Your sustainability team built into the platform with software, services, and expert support.",
    bullets: [
      "Everything in Navigator",
      "Personalized onboarding and data strategy",
      "Data formatting and upload help",
      "Carbon bookkeeping and advisory guidance",
      "Reporting support and Net Zero help",
      "OnePage collaboration workspace"
    ],
    primaryCta: { label: "Book Demo", onClick: openSales },
    secondaryCta: {
      label: "Learn More",
      href: `${ACLYMATE_URL}/products/aclymate-one`
    }
  },
  {
    id: "aclymate-custom",
    name: "Aclymate Custom",
    label: "Tailored Plan",
    bestFor: "Larger or more complex sustainability programs",
    description:
      "A tailored sustainability solution for advanced reporting, certifications, integrations, and program needs.",
    bullets: [
      "Everything in Aclymate One",
      "Tailored program design",
      "Advanced reporting support",
      "Complex certification support",
      "Strategic advisory support",
      "Configured to your organization's needs"
    ],
    primaryCta: { label: "Talk to Sales", onClick: openSales },
    secondaryCta: { label: "Contact Us", onClick: openSales }
  }
];

const readEntryAnchor = () => {
  if (typeof window === "undefined") return "";
  const hash = window.location.hash || "";
  return hash.slice(1);
};

const UpgradePage = () => {
  const [isExpertOpen, setIsExpertOpen] = useState(false);
  const entryAnchor = readEntryAnchor();
  const { hash } = useLocation();

  useEffect(() => {
    analyticsTrack("explorer_upgrade_page_viewed", {
      entryAnchor: window.location.hash || ""
    });
  }, []);

  useEffect(() => {
    if (!hash) return;
    const id = hash.slice(1);
    const el = document.getElementById(id);
    if (!el) return;
    window.requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [hash]);

  const openSales = () => {
    setIsExpertOpen(true);
  };

  const handlePlanCtaClick = (plan, cta) =>
    analyticsTrack("explorer_upgrade_page_cta_clicked", {
      plan: plan.name,
      cta: cta.label
    });

  const plans = buildPlans(openSales);

  return (
    <DashboardLayout
      action={<BackToDashboard />}
      showScoutSidePanel={false}
      hideUpgradeBanner
    >
      {isExpertOpen && (
        <CommunicationCenter
          initialTab="sales"
          isSlideOpen={isExpertOpen}
          setIsSlideOpen={setIsExpertOpen}
        />
      )}
      <Hero
        headline="Your Sustainability Software and Team for Reporting, Certifications & Program Management"
        subheadline="From the core platform to full software-plus-services and tailored programs, Aclymate gives you the right mix of software, services, and guidance to grow beyond Explorer."
        description="No sustainability team required for most plans."
        primaryCta={{
          label: "Start Navigator Trial",
          href: "/account-creation/company"
        }}
        secondaryCta={{ label: "See Demo", href: `${ACLYMATE_URL}/demo` }}
        tertiaryLink={{ label: "Talk to Sales", onClick: openSales }}
        visual={<HeroCalloutVisual />}
      />

      <PricingGrid
        title="Choose the plan that fits your team"
        description="Move into the core platform, add expert services, or choose a tailored program for more complex sustainability needs."
        plans={plans}
        onPlanCtaClick={handlePlanCtaClick}
      />

      <WhyComparison
        eyebrow="Quick Comparison"
        title="Compare plans at a glance"
        description="See the major differences across the four Aclymate plans. Keep offsets and RECs visible, but lead with footprint estimation, carbon accounting, reporting, sustainability management, services, and credibility."
        columns={COMPARISON_COLUMNS}
        rows={COMPARISON_ROWS}
        values={COMPARISON_VALUES}
        primaryCta={{
          label: "Start Navigator Trial",
          href: "/account-creation/company"
        }}
      />

      <FinalCTA
        variant="contrast"
        headline="Start where you are. Grow with Aclymate."
        body="Whether you need the core platform, software plus expert services, or a tailored sustainability program, Aclymate gives you the software and team to move forward."
        primaryCta={{
          label: "Start Navigator Trial",
          href: "/account-creation/company"
        }}
        secondaryCta={{ label: "See Demo", href: `${ACLYMATE_URL}/demo` }}
      />
      <ScoutFab surface="upgrade" surfaceContext={{ entryAnchor }} />
    </DashboardLayout>
  );
};
export default UpgradePage;
